import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule, IonModal, ToastController } from '@ionic/angular';
import { BerService } from '../ber.service';
import { BER_OPCIOK, honapNev } from '../ber.opciok';
import { BerJuttatasApi, ftSzoveg, szazalek, tizedes } from '../juttatas/juttatas.api';
import { JuttatasParamComponent } from '../juttatas/juttatas-param.component';

/**
 *  CÉGTELEFON MAGÁNCÉLÚ HASZNÁLATA (RLB: Számfejtés → Cégtelefon magáncélú használata).
 *
 *  Havonta munkavállalónként: a telefon számlaértéke, a magáncélú rész – tételesen (hívásrészletező
 *  alapján) vagy egyszerűsítve a számla 20%-a –, a munkavállaló megtérítése. A meg nem térített
 *  magáncélú rész egyes meghatározott juttatás (Szja tv. 70. § (1) b)): adóalap = érték × 1,18,
 *  erre SZJA és szocho (KIVA-alanynál szocho helyett a KIVA-alapba kerül).
 *  A táblázat élőben számol (előnézet); a mentéskor a kiszolgáló számolja és tárolja a közterheket.
 */
@Component({
  selector: 'app-ber-cegtelefon',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, JuttatasParamComponent],
  templateUrl: './cegtelefon.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss', './cegtelefon.component.scss'],
})
export class CegtelefonComponent implements OnInit {
  @ViewChild('paramModal') paramModal: IonModal;
  evek: number[] = [];
  honapok = BER_OPCIOK.honapok;
  honapNev = honapNev;
  ft = ftSzoveg;
  szazalek = szazalek;
  tizedes = tizedes;

  sorok: any[] = [];           // szerkeszthető sorok (munkavállalónként legalább egy)
  alkalmazottak: any[] = [];
  param: any = null;
  kivas = false;
  valtozott = false;
  mentes = false;
  betoltve = false;

  constructor(private ber: BerService, private api: BerJuttatasApi, private toast: ToastController) {}

  async ngOnInit() {
    await this.ber.init();
    const ev = new Date().getFullYear();
    this.evek = [ev + 1, ev, ev - 1, ev - 2, ev - 3];
    await this.betolt();
  }

  get aktiv() { return this.ber.aktiv; }
  async evValt(ev: number) { await this.ber.setAktiv({ ev }); await this.betolt(); }
  async hoValt(ho: number) { await this.ber.setAktiv({ ho }); await this.betolt(); }

  async betolt() {
    this.betoltve = false; this.valtozott = false;
    if (!this.aktiv.ugyfelId) return;
    try {
      const r: any = await this.api.get(`/telefon/${this.aktiv.ugyfelId}?ev=${this.aktiv.ev}&ho=${this.aktiv.ho}`);
      this.param = r.param; this.kivas = !!r.kivas; this.alkalmazottak = r.alkalmazottak || [];
      this.sorok = this.racsEpit(r.alkalmazottak || [], r.sorok || []);
      this.betoltve = true;
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült betölteni.'); }
  }

  /** Minden munkavállalónak legalább egy sor; a meglévő telefon-sorok a helyükön. */
  private racsEpit(alkalmazottak: any[], meglevo: any[]): any[] {
    const out: any[] = [];
    for (const a of alkalmazottak) {
      const sajat = meglevo.filter((s) => s.alkalmazott_id === a.id);
      if (sajat.length) sajat.forEach((s) => out.push(this.sorbol(a, s)));
      else out.push(this.sorbol(a, null));
    }
    return out;
  }
  private sorbol(a: any, s: any) {
    return {
      id: s?.id || null, alkalmazott_id: a.id, nev: a.nev, adoazonosito: a.adoazonosito,
      koltseg: s ? Number(s.koltseg) || 0 : null, szamitas: s?.szamitas || 'egyszerusitett',
      magan_resz: s ? Number(s.magan_resz) || 0 : null, megterites: s ? Number(s.megterites) || 0 : null,
      megjegyzes: s?.megjegyzes || '',
    };
  }

  /* ── élő számítás (a kiszolgálóéval azonos képlet) ── */
  magan(s: any): number {
    const k = Math.max(0, Number(s.koltseg) || 0);
    return s.szamitas === 'tetelesen' ? Math.min(k, Math.max(0, Number(s.magan_resz) || 0)) : Math.round(k * (this.param?.telefon_magan_hanyad ?? 0.2));
  }
  ertek(s: any): number { return Math.max(0, this.magan(s) - Math.max(0, Number(s.megterites) || 0)); }
  adoalap(s: any): number { return Math.round(this.ertek(s) * (this.param?.adoalap_szorzo ?? 1.18)); }
  szja(s: any): number { return Math.round(this.adoalap(s) * (this.param?.szja_kulcs ?? 0.15)); }
  szocho(s: any): number { return this.kivas ? 0 : Math.round(this.adoalap(s) * (this.param?.szocho_kulcs ?? 0.13)); }
  ossz(f: (s: any) => number): number { return this.sorok.reduce((a, s) => a + f(s), 0); }
  get osszesen() {
    return {
      koltseg: this.ossz((s) => Number(s.koltseg) || 0), magan: this.ossz((s) => this.magan(s)),
      megterites: this.ossz((s) => Math.max(0, Number(s.megterites) || 0)), ertek: this.ossz((s) => this.ertek(s)),
      adoalap: this.ossz((s) => this.adoalap(s)), szja: this.ossz((s) => this.szja(s)), szocho: this.ossz((s) => this.szocho(s)),
    };
  }

  jelol() { this.valtozott = true; }
  ujVonal(s: any) {
    const i = this.sorok.indexOf(s);
    this.sorok.splice(i + 1, 0, { ...this.sorbol({ id: s.alkalmazott_id, nev: s.nev, adoazonosito: s.adoazonosito }, null) });
  }
  torolSor(s: any) {
    // A mentett sor a 0 költséggel törlődik a mentéskor; az új, üres sor azonnal eltűnik.
    if (s.id) { s.koltseg = 0; s.magan_resz = 0; s.megterites = 0; this.valtozott = true; return; }
    const tobb = this.sorok.filter((x) => x.alkalmazott_id === s.alkalmazott_id).length > 1;
    if (tobb) this.sorok.splice(this.sorok.indexOf(s), 1); else { s.koltseg = null; s.magan_resz = null; s.megterites = null; }
  }

  async ment() {
    const sorok = this.sorok.filter((s) => s.id || (Number(s.koltseg) || 0) > 0).map((s) => ({
      id: s.id, alkalmazott_id: s.alkalmazott_id, koltseg: Number(s.koltseg) || 0, szamitas: s.szamitas,
      magan_resz: Number(s.magan_resz) || 0, megterites: Number(s.megterites) || 0, megjegyzes: s.megjegyzes || null,
    }));
    this.mentes = true;
    try {
      const r: any = await this.api.post(`/telefon/${this.aktiv.ugyfelId}?ev=${this.aktiv.ev}&ho=${this.aktiv.ho}`, { sorok });
      this.ok(`Mentve (${r.mentve} sor${r.torolve ? `, ${r.torolve} törölve` : ''}).`);
      await this.betolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Mentési hiba.'); }
    finally { this.mentes = false; }
  }

  /** Az előző hónap számlaértékei és beállításai azokhoz, akiknél ebben a hónapban még nincs adat (nem ment). */
  async elozoHonap() {
    const ev = this.aktiv.ho === 1 ? this.aktiv.ev - 1 : this.aktiv.ev;
    const ho = this.aktiv.ho === 1 ? 12 : this.aktiv.ho - 1;
    try {
      const r: any = await this.api.get(`/telefon/${this.aktiv.ugyfelId}?ev=${ev}&ho=${ho}`);
      let n = 0;
      for (const e of (r.sorok || [])) {
        const cel = this.sorok.find((s) => s.alkalmazott_id === e.alkalmazott_id && !s.id && !(Number(s.koltseg) > 0));
        if (!cel) continue;
        Object.assign(cel, { koltseg: Number(e.koltseg) || 0, szamitas: e.szamitas || 'egyszerusitett', magan_resz: Number(e.magan_resz) || 0,
          megterites: Number(e.megterites) || 0 });
        n++;
      }
      if (n) { this.valtozott = true; this.ok(`${n} sor átvéve (${honapNev(ho)}) – ellenőrizd, majd mentsd.`); }
      else this.ok(`${honapNev(ho)}: nincs átvehető adat.`);
    } catch { this.hiba('Az előző hónap nem tölthető be.'); }
  }

  async nyomtat() {
    const sorok = this.sorok.filter((s) => (Number(s.koltseg) || 0) > 0);
    const oszlopok = [
      { k: 'nev', c: 'Munkavállaló', szeles: true }, { k: 'adoazonosito', c: 'Adóazonosító' },
      { k: 'koltseg', c: 'Számlaérték', num: true }, { k: 'szamitas', c: 'Számítás' },
      { k: 'magan', c: 'Magáncélú rész', num: true }, { k: 'megterites', c: 'Megtérítés', num: true },
      { k: 'ertek', c: 'Juttatás értéke', num: true }, { k: 'adoalap', c: 'Adóalap', num: true },
      { k: 'szja', c: 'SZJA', num: true }, { k: 'szocho', c: this.kivas ? 'KIVA-alapba' : 'Szocho', num: true },
    ];
    const ertek = (s: any, o: any): string => {
      switch (o.k) {
        case 'nev': return s.nev; case 'adoazonosito': return s.adoazonosito || '';
        case 'koltseg': return ftSzoveg(s.koltseg); case 'szamitas': return s.szamitas === 'tetelesen' ? 'tételes' : `${szazalek(this.param?.telefon_magan_hanyad)}%`;
        case 'magan': return ftSzoveg(this.magan(s)); case 'megterites': return ftSzoveg(s.megterites);
        case 'ertek': return ftSzoveg(this.ertek(s)); case 'adoalap': return ftSzoveg(this.adoalap(s));
        case 'szja': return ftSzoveg(this.szja(s)); case 'szocho': return ftSzoveg(this.kivas ? this.adoalap(s) : this.szocho(s));
      }
      return '';
    };
    const ossz: Record<string, (s: any) => number> = {
      koltseg: (s) => Number(s.koltseg) || 0, magan: (s) => this.magan(s), megterites: (s) => Number(s.megterites) || 0,
      ertek: (s) => this.ertek(s), adoalap: (s) => this.adoalap(s), szja: (s) => this.szja(s),
      szocho: (s) => (this.kivas ? this.adoalap(s) : this.szocho(s)),
    };
    await this.api.nyomtat('Cégtelefon magáncélú használata', [['Időszak', `${this.aktiv.ev}. ${honapNev(this.aktiv.ho)}`]],
      oszlopok, sorok, ertek, (o) => (ossz[o.k] ? ftSzoveg(sorok.reduce((a, s) => a + ossz[o.k](s), 0)) : ''));
  }

  async parameterek() { await this.paramModal.present(); }
  /** Új kulcsok után az előnézet már azokkal számol; a tárolt sorokat a Mentés frissíti. */
  async paramMentve() {
    await this.paramModal.dismiss();
    const vanMentett = this.sorok.some((s) => s.id);
    await this.betolt();
    if (vanMentett) { this.valtozott = true; this.ok('A mentett sorokat a Mentés számolja újra az új paraméterekkel.'); }
  }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 1800, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 2600, color: 'danger' })).present(); }
}
